import type { TravellerCount } from '../../lib/travellers';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { Button, Text } from '@/shared/ui';
import css from './traveller-count-list.module.scss';

export type TravellerTypeOption = {
  id: string;
  title: string;
};

type TravellerCountListProps = {
  value: TravellerCount[];
  onChange: (next: TravellerCount[]) => void;
  travellerTypes: TravellerTypeOption[];
  disabled?: boolean;
  // Optional copy override — settings page calls them "Travelers",
  // chapter editor wants the action button hidden (the user supplied
  // a different add control). Defaults match legacy "Add Travelers".
  addLabel?: string;
  // Lowest acceptable value for any single row. Settings page enforces
  // 1 (every selected type stays positive); the per-item / per-chapter
  // surfaces allow 0 and let `filterPositiveTravellers` drop empties
  // on save.
  minCount?: number;
  // Hides the empty placeholder text when the consumer renders its own
  // (e.g. "No traveller types configured" for permission-gated surfaces).
  emptyPlaceholder?: string | null;
};

// Shared traveller counter list. Renders one row per `value` entry
// (title + minus/input/plus + remove) and an "Add Travelers" button
// that appends the first unused type. Settings page, item Travellers
// modal, and chapter editor all bind against the same `TravellerCount[]`
// shape so the same component covers every editing surface.
export const TravellerCountList = ({
  value,
  onChange,
  travellerTypes,
  disabled = false,
  addLabel = 'Add Travelers',
  minCount = 0,
  emptyPlaceholder = null,
}: TravellerCountListProps) => {
  const setCount = (index: number, raw: number) => {
    const safe = Math.max(minCount, Math.round(raw));
    onChange(
      value.map((entry, i) => (i === index ? { ...entry, count: safe } : entry))
    );
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const usedIds = new Set(value.map((v) => v.travelerTypeId));
  const available = travellerTypes.filter((t) => !usedIds.has(t.id));

  const add = () => {
    const next = available[0];
    if (!next) return;
    onChange([
      ...value,
      {
        travelerTypeId: next.id,
        title: next.title,
        count: Math.max(1, minCount),
      },
    ]);
  };

  return (
    <div>
      <div className={css.list}>
        {value.length === 0 && emptyPlaceholder ? (
          <Text className={css.empty} size="sm" variant="muted">
            {emptyPlaceholder}
          </Text>
        ) : null}
        {value.map((entry, index) => (
          <div className={css.row} key={entry.travelerTypeId}>
            <Text>{entry.title || entry.travelerTypeId}</Text>
            <div className={css.counter}>
              <button
                aria-label={`Decrement ${entry.title}`}
                className={css.counterBtn}
                disabled={disabled || entry.count <= minCount}
                onClick={() => setCount(index, entry.count - 1)}
                type="button"
              >
                <Minus size={14} />
              </button>
              <input
                aria-label={`${entry.title} count`}
                className={css.counterInput}
                disabled={disabled}
                min={minCount}
                onChange={(e) =>
                  setCount(index, Number.parseInt(e.target.value, 10) || 0)
                }
                type="number"
                value={entry.count}
              />
              <button
                aria-label={`Increment ${entry.title}`}
                className={css.counterBtn}
                disabled={disabled}
                onClick={() => setCount(index, entry.count + 1)}
                type="button"
              >
                <Plus size={14} />
              </button>
            </div>
            <Button
              aria-label={`Remove ${entry.title || 'traveller type'}`}
              disabled={disabled}
              onClick={() => remove(index)}
              size="sm"
              type="button"
              variant="ghost"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
      </div>
      <Button
        disabled={disabled || available.length === 0}
        icon={<Plus style={{ height: 14, width: 14 }} />}
        onClick={add}
        size="sm"
        style={{ marginTop: 12 }}
        type="button"
      >
        {addLabel}
      </Button>
    </div>
  );
};
